import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Check, Close, Visibility, VisibilityOff } from '@mui/icons-material';

export interface PasswordValidation {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

interface PasswordFieldProps {
  onPasswordChange: (password: string) => void;
  onConfirmPasswordChange?: (confirmPassword: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  showConfirmField?: boolean;
  showValidation?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  initialPassword?: string;
  initialConfirmPassword?: string;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  onPasswordChange,
  onConfirmPasswordChange,
  onValidationChange,
  showConfirmField = true,
  showValidation = true,
  disabled = false,
  autoFocus = false,
  margin = 'normal',
  initialPassword = '',
  initialConfirmPassword = '',
}) => {
  const [password, setPassword] = useState(initialPassword);
  const [confirmPassword, setConfirmPassword] = useState(initialConfirmPassword);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    onPasswordChange(newPassword);
  };

  const handleConfirmPasswordChange = (newConfirmPassword: string) => {
    setConfirmPassword(newConfirmPassword);
    onConfirmPasswordChange?.(newConfirmPassword);
  };

  const handlePasswordValidation = (validation: PasswordValidation, passwordsMatch: boolean) => {
    const allValid = Object.values(validation).every(Boolean) && passwordsMatch;
    onValidationChange?.(allValid);
  };

  // Update password validation in real-time
  useEffect(() => {
    const validation = {
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password), //  special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    setPasswordValidation(validation);

    const passwordsMatch = !showConfirmField || password === confirmPassword;
    handlePasswordValidation(validation, passwordsMatch);
  }, [password, confirmPassword, showConfirmField]);

  const renderPasswordRequirement = (isValid: boolean, text: string) => (
    <ListItem dense sx={{ py: 0 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {isValid ? (
          <Check sx={{ fontSize: 14, color: 'success.main' }} />
        ) : (
          <Close sx={{ fontSize: 14, color: 'error.main' }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={text}
        sx={{
          '& .MuiListItemText-primary': {
            fontSize: '0.75rem',
            lineHeight: 1,
            color: isValid ? 'success.main' : 'text.secondary',
          },
        }}
      />
    </ListItem>
  );

  //  const isAllValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = !showConfirmField || password === confirmPassword;

  return (
    <Box>
      <TextField
        fullWidth
        type={showCurrentPassword ? 'text' : 'password'}
        label="Password"
        value={password}
        onChange={(e) => handlePasswordChange(e.target.value)}
        margin={margin}
        required
        autoFocus={autoFocus}
        disabled={disabled}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Password Requirements */}
      <Box sx={{ mt: 1, mb: showConfirmField ? 2 : 0 }}>
        {showValidation && password && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ fontSize: 'small', mb: 0 }}
            >
              Password Requirements:
            </Typography>
            <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, py: 1 }}>
              {renderPasswordRequirement(passwordValidation.minLength, 'At least 8 characters')}
              {renderPasswordRequirement(passwordValidation.uppercase, 'One uppercase letter')}
              {renderPasswordRequirement(passwordValidation.lowercase, 'One lowercase letter')}
              {renderPasswordRequirement(passwordValidation.number, 'One number')}
              {renderPasswordRequirement(passwordValidation.special, 'One special character')}
            </List>
          </>
        )}
      </Box>
      {showConfirmField && (
        <TextField
          fullWidth
          type={showCurrentPassword ? 'text' : 'password'}
          label="Confirm Password"
          value={confirmPassword}
          onChange={(e) => handleConfirmPasswordChange(e.target.value)}
          margin={margin}
          required
          disabled={disabled}
          error={!!confirmPassword && !passwordsMatch}
          helperText={confirmPassword && !passwordsMatch ? 'Passwords do not match' : ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default PasswordField;
